import { useEffect } from 'react';

/**
 * After the page finishes loading, scrolls a specific comment or reply
 * into view (used when the URL/props point at a deep-linked thread).
 *
 * Why the retry loop?
 *   - The comment node may mount a frame after `isLoading` flips to false.
 *   - OverlayScrollbars needs the target element to exist before
 *     `scrollIntoView` can drive the inner viewport.
 *   So we poll for the id every 80ms (max 40 attempts = ~3.2s) before
 *   giving up. Start is deferred 120ms to give the list time to render.
 */
const useScrollToTarget = ({ isLoading, targetCommentId, targetReplyId }) => {
  useEffect(() => {
    if (isLoading || (!targetCommentId && !targetReplyId)) return undefined;

    const targetDomId = targetReplyId
      ? `reply-${String(targetReplyId)}`
      : `comment-${String(targetCommentId)}`;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(targetDomId);
      if (el) {
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        setTimeout(tryScroll, 80);
      }
    };

    const start = setTimeout(tryScroll, 120);
    return () => {
      cancelled = true;
      clearTimeout(start);
    };
  }, [isLoading, targetCommentId, targetReplyId]);
};

export default useScrollToTarget;
